import { useEffect, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { msalConfig } from '@/config/authConfig';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ProgressSpinner } from '@/components/ui';
import { LoginPage } from '@/pages/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { UserGroupsPage } from '@/pages/UserGroupsPage';
import { UserManagementPage } from '@/pages/UserManagementPage';
import { CompanyManagementPage } from '@/pages/CompanyManagementPage';
import { ReportsPage } from '@/pages/ReportsPage';
import ProfilePage from '@/pages/ProfilePage';
import { useThemeStore } from '@/store';
import ComponentShowcasePage from './pages/ComponentShowcasePage';
import FixturesPage from '@/pages/FixturesPage';
import FixtureDetailsPage from '@/pages/FixtureDetailsPage';
import RecentFixturesPage from '@/pages/RecentFixturesPage';
import { PositionListPage } from '@/pages/PositionListPage';
import { PositionDetailsPage } from '@/pages/PositionDetailsPage';
import { CargoListPage } from '@/pages/CargoListPage';
import { CargoDetailsPage } from '@/pages/CargoDetailsPage';
import { BalticPage } from '@/pages/BalticPage';
import { BunkerPricesPage } from '@/pages/BunkerPricesPage';
import { DailyReportsPage } from '@/pages/DailyReportsPage';
import { CargoCountPage } from '@/pages/CargoCountPage';
import { FixedDaysPage } from '@/pages/FixedDaysPage';
import { PowerBIReportPage } from '@/pages/PowerBIReportPage';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AccessDenied } from '@/pages/AccessDenied';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { UserRole } from '@/permissions/types';
import { PoolReportsPage } from '@/pages/PoolReportsPage';
import { PoolReportsDownloadPage } from '@/pages/PoolReportsDownloadPage';
import { PoolReportsSummaryPage } from '@/pages/PoolReportsSummaryPage';
import { PoolReportsContributionPage } from '@/pages/PoolReportsContributionPage';
import { PoolReportsContributionOwnerPage } from '@/pages/PoolReportsContributionOwnerPage';
import { PoolReportsVesselDetailsPage } from '@/pages/PoolReportsVesselDetailsPage';
import { PoolReportsNetAllocationPage } from '@/pages/PoolReportsNetAllocationPage';
import { PoolReportsNetAllocationOwnerPage } from '@/pages/PoolReportsNetAllocationOwnerPage';
import PoolReportsOffhirePage from '@/pages/PoolReportsOffhirePage';
import PoolReportsOffhireOwnerPage from '@/pages/PoolReportsOffhireOwnerPage';
import { PoolReportsOffhireVesselBreakdownPage } from '@/pages/PoolReportsOffhireVesselBreakdownPage';
import { PoolReportsDistributionPage } from '@/pages/PoolReportsDistributionPage';
import { PoolReportsDistributionOwnerPage } from '@/pages/PoolReportsDistributionOwnerPage';
import { DistributionSchedulePage } from '@/pages/DistributionSchedulePage';
import { PoolPointCalculationPage } from '@/pages/PoolPointCalculationPage';
import { ToastProvider, useToast } from '@/contexts/ToastContext';
import { QueryProvider } from '@/providers/QueryProvider';
import { tokenRefreshService } from '@/services/tokenRefreshService';
import { CookieConsentHandler } from '@/components/layout/CookieConsentHandler';
import { PwaInstallHandler } from '@/components/layout/PwaInstallHandler';
import { RefreshPage } from '@/pages/RefreshPage';

// Session expiration toast helper component
function SessionNotificationSetup() {
  const { showToast } = useToast();

  useEffect(() => {
    // Expose global function for axios interceptor to trigger toast
    (window as { showSessionExpiredToast?: () => void }).showSessionExpiredToast = () => {
      showToast('Your session has expired. Please login again.', {
        variant: 'warning',
        title: 'Session Expired',
        duration: 5000,
      });
    };

    return () => {
      delete (window as { showSessionExpiredToast?: () => void }).showSessionExpiredToast;
    };
  }, [showToast]);

  return null;
}

function ScrollToTopOnNavigation() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
}

// Initialize MSAL instance - preserve across HMR reloads
let msalInstance: IPublicClientApplication = (window as { msalInstance?: IPublicClientApplication })
  .msalInstance!;

if (!msalInstance) {
  msalInstance = new PublicClientApplication(msalConfig);
  (window as { msalInstance?: IPublicClientApplication }).msalInstance = msalInstance;
}

const ACTIVE_ACCOUNT_KEY = 'msalHomeAccountId';

const getTenantIdFromAuthority = () => {
  const authority = msalConfig.auth?.authority || '';
  const parts = authority.split('/').filter(Boolean);
  return parts[parts.length - 1]?.toLowerCase() || '';
};

const selectActiveAccount = () => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return;
  }

  const savedHomeId = localStorage.getItem(ACTIVE_ACCOUNT_KEY);
  const tenantId = getTenantIdFromAuthority();

  const savedAccount = savedHomeId
    ? accounts.find((account) => account.homeAccountId === savedHomeId)
    : undefined;

  const tenantAccount = tenantId
    ? accounts.find((account) => account.tenantId?.toLowerCase() === tenantId)
    : undefined;

  const account = savedAccount || tenantAccount || accounts[0];
  if (account) {
    msalInstance.setActiveAccount(account);
  }
};

function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize theme on mount
  useEffect(() => {
    const theme = useThemeStore.getState().theme;
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, []);

  useEffect(() => {
    const initializeMsal = async () => {
      // Check if already initialized (prevents HMR re-initialization)
      const windowObj = window as { msalInitialized?: boolean };
      if (windowObj.msalInitialized) {
        // Restore active account on HMR (critical fix for 401 errors)
        selectActiveAccount();

        // Ensure token refresh service is running with current MSAL instance
        if (!tokenRefreshService.isRunning()) {
          tokenRefreshService.start(msalInstance);
        } else {
          // Update the MSAL instance reference in case it changed
          tokenRefreshService.start(msalInstance);
        }

        setIsInitialized(true);
        return;
      }

      await msalInstance.initialize();

      // Make MSAL instance available globally for services
      (window as { msalInstance?: IPublicClientApplication }).msalInstance = msalInstance;
      windowObj.msalInitialized = true;

      // Handle redirect promise first, before routing
      try {
        const response = await msalInstance.handleRedirectPromise();
        if (response !== null) {
          // Handle successful authentication
          const account = response.account;
          msalInstance.setActiveAccount(account);
          if (account?.homeAccountId) {
            localStorage.setItem(ACTIVE_ACCOUNT_KEY, account.homeAccountId);
          }
        } else {
          // Check if there are any accounts
          selectActiveAccount();
        }
      } catch (error) {
        // Error handling redirect
      }

      setIsInitialized(true);
    };

    initializeMsal();

    // Register event callbacks
    const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
        if (account?.homeAccountId) {
          localStorage.setItem(ACTIVE_ACCOUNT_KEY, account.homeAccountId);
        }

        // Start proactive token refresh after successful login
        if (!tokenRefreshService.isRunning()) {
          tokenRefreshService.start(msalInstance);
        }
      }

      if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        if (!msalInstance.getActiveAccount() && payload.account) {
          msalInstance.setActiveAccount(payload.account);
          if (payload.account?.homeAccountId) {
            localStorage.setItem(ACTIVE_ACCOUNT_KEY, payload.account.homeAccountId);
          }
        }
      }

      // Stop token refresh service on logout
      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        tokenRefreshService.stop();
      }
    });

    // HMR: Preserve MSAL state across hot reloads
    if (import.meta.hot) {
      import.meta.hot.dispose(() => {
        // Don't clean up on HMR - preserve auth state
      });
    }

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
      // Only clean up on actual unmount (not HMR)
      if (!import.meta.hot) {
        tokenRefreshService.stop();
      }
    };
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-dark-950">
        <ProgressSpinner size="xl" text="Initializing..." />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <QueryProvider>
        <MsalProvider instance={msalInstance}>
          <ToastProvider position="top-right" defaultDuration={5000}>
            <SessionNotificationSetup />
            <CookieConsentHandler />
            <PwaInstallHandler />
            <BrowserRouter>
              <ScrollToTopOnNavigation />
              <Routes>
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/refresh" element={<RefreshPage />} />
                <Route path="/accessdenied" element={<AccessDenied />} />
                <Route path="/notfound" element={<NotFoundPage />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user-groups"
                  element={
                    <ProtectedRoute roles={[UserRole.USER_GROUPS]}>
                      <UserGroupsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute roles={[UserRole.USERS]}>
                      <UserManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/companies"
                  element={
                    <ProtectedRoute roles={[UserRole.COMPANIES]}>
                      <CompanyManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/files"
                  element={
                    <ProtectedRoute roles={[UserRole.REPORTS_FILES]}>
                      <ReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/distribution-schedule"
                  element={
                    <ProtectedRoute roles={[UserRole.REPORTS_FILES]}>
                      <DistributionSchedulePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/pool-point-calculation"
                  element={
                    <ProtectedRoute roles={[UserRole.REPORTS_FILES]}>
                      <PoolPointCalculationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user-profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/components"
                  element={
                    <ProtectedRoute>
                      <ComponentShowcasePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fixtures"
                  element={
                    <ProtectedRoute roles={[UserRole.FIXTURES]}>
                      <FixturesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recent-fixtures"
                  element={
                    <ProtectedRoute roles={[UserRole.RECENT_FIXTURES]}>
                      <RecentFixturesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recent-fixtures/:fixtureId"
                  element={
                    <ProtectedRoute roles={[UserRole.RECENT_FIXTURES]}>
                      <FixtureDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fixtures/:fixtureId"
                  element={
                    <ProtectedRoute roles={[UserRole.FIXTURES]}>
                      <FixtureDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/position-list"
                  element={
                    <ProtectedRoute roles={[UserRole.POSITION_LIST]}>
                      <PositionListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/position-list/:area/:vesselId"
                  element={
                    <ProtectedRoute roles={[UserRole.POSITION_LIST]}>
                      <PositionDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cargo-list"
                  element={
                    <ProtectedRoute roles={[UserRole.CARGO_LIST]}>
                      <CargoListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cargo-list/:id"
                  element={
                    <ProtectedRoute roles={[UserRole.CARGO_LIST]}>
                      <CargoDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/baltic"
                  element={
                    <ProtectedRoute roles={[UserRole.BALTIC]}>
                      <BalticPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bunker-prices"
                  element={
                    <ProtectedRoute roles={[UserRole.BUNKER_PRICES]}>
                      <BunkerPricesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/daily"
                  element={
                    <ProtectedRoute roles={[UserRole.DAILY_REPORT]}>
                      <DailyReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cargo-count"
                  element={
                    <ProtectedRoute roles={[UserRole.CARGO_COUNT]}>
                      <CargoCountPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/fixed-days"
                  element={
                    <ProtectedRoute roles={[UserRole.FIXED_DAYS]}>
                      <FixedDaysPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/bi/:reportId/:type"
                  element={
                    <ProtectedRoute
                      roles={[
                        UserRole.POWERBI_BASIC,
                        UserRole.POWERBI_PRIVILEGED,
                        UserRole.POWERBI_PRIVILEGED_KPC,
                      ]}
                    >
                      <PowerBIReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/bi/:reportId/:type/:endpoint"
                  element={
                    <ProtectedRoute
                      roles={[
                        UserRole.POWERBI_BASIC,
                        UserRole.POWERBI_PRIVILEGED,
                        UserRole.POWERBI_PRIVILEGED_KPC,
                      ]}
                    >
                      <PowerBIReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/yearly/*"
                  element={
                    <ProtectedRoute roles={[UserRole.ANNUAL_REPORTS]}>
                      <PoolReportsPage />
                    </ProtectedRoute>
                  }
                >
                  <Route path="summary" element={<PoolReportsSummaryPage />} />
                  <Route path="contribution" element={<PoolReportsContributionPage />} />
                  <Route
                    path="contribution/owner/:ownerId"
                    element={<PoolReportsContributionOwnerPage />}
                  />
                  <Route
                    path="contribution/owner/:ownerId/:vesselId"
                    element={<PoolReportsVesselDetailsPage />}
                  />
                  <Route path="net-allocation" element={<PoolReportsNetAllocationPage />} />
                  <Route
                    path="net-allocation/owner/:ownerId"
                    element={<PoolReportsNetAllocationOwnerPage />}
                  />
                  <Route
                    path="net-allocation/owner/:ownerId/:vesselId"
                    element={<PoolReportsVesselDetailsPage />}
                  />
                  <Route path="distribution" element={<PoolReportsDistributionPage />} />
                  <Route
                    path="distribution/owner/:ownerId"
                    element={<PoolReportsDistributionOwnerPage />}
                  />
                  <Route
                    path="distribution/owner/:ownerId/:vesselId"
                    element={<PoolReportsVesselDetailsPage />}
                  />
                  <Route path="offhire" element={<PoolReportsOffhirePage />} />
                  <Route path="offhire/owner/:ownerId" element={<PoolReportsOffhireOwnerPage />} />
                  <Route
                    path="offhire/owner/:ownerId/:vesselId"
                    element={<PoolReportsOffhireVesselBreakdownPage />}
                  />
                  <Route path="download" element={<PoolReportsDownloadPage />} />
                </Route>
                <Route
                  path="/reports/monthly/*"
                  element={
                    <ProtectedRoute roles={[UserRole.MONTHLY_REPORTS]}>
                      <PoolReportsPage />
                    </ProtectedRoute>
                  }
                >
                  <Route path="summary" element={<PoolReportsSummaryPage />} />
                  <Route path="contribution" element={<PoolReportsContributionPage />} />
                  <Route
                    path="contribution/owner/:ownerId"
                    element={<PoolReportsContributionOwnerPage />}
                  />
                  <Route
                    path="contribution/owner/:ownerId/:vesselId"
                    element={<PoolReportsVesselDetailsPage />}
                  />
                  <Route path="net-allocation" element={<PoolReportsNetAllocationPage />} />
                  <Route
                    path="net-allocation/owner/:ownerId"
                    element={<PoolReportsNetAllocationOwnerPage />}
                  />
                  <Route
                    path="net-allocation/owner/:ownerId/:vesselId"
                    element={<PoolReportsVesselDetailsPage />}
                  />
                  <Route path="distribution" element={<PoolReportsDistributionPage />} />
                  <Route
                    path="distribution/owner/:ownerId"
                    element={<PoolReportsDistributionOwnerPage />}
                  />
                  <Route
                    path="distribution/owner/:ownerId/:vesselId"
                    element={<PoolReportsVesselDetailsPage />}
                  />
                  <Route path="offhire" element={<PoolReportsOffhirePage />} />
                  <Route path="offhire/owner/:ownerId" element={<PoolReportsOffhireOwnerPage />} />
                  <Route
                    path="offhire/owner/:ownerId/:vesselId"
                    element={<PoolReportsOffhireVesselBreakdownPage />}
                  />
                  <Route path="download" element={<PoolReportsDownloadPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/notfound" replace />} />
              </Routes>
            </BrowserRouter>
          </ToastProvider>
        </MsalProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
}
export default App;
